import type {
  PreviewEngineActions,
  RotationControlSignals,
  RotationEngineStatus,
} from 'gcf-player/shared-utils';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSharedUtilsFromGcfPlayer } from 'src/hooks/useSharedUtilsFromGcfPlayer';

import { extractRotationItems } from '../../MenuBoardPlayback.helper';
import type { IMenuBoardPlayback } from './MenuBoardPlaybackController.interface';

const PositionSync = (props: {
  signals: RotationControlSignals;
  onActivePositionChange?: (position: number) => void;
}) => {
  const { signals, onActivePositionChange } = props;
  const prevPositionRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevPositionRef.current === signals.activePosition) {
      return;
    }

    if (prevPositionRef.current !== undefined) {
      onActivePositionChange?.(signals.activePosition);
    }

    prevPositionRef.current = signals.activePosition;
  }, [signals.activePosition, onActivePositionChange]);

  return null;
};

export const MenuBoardPlaybackController = (props: IMenuBoardPlayback) => {
  const {
    previewData,
    initialPosition,
    onStatusChange,
    onActivePositionChange,
    children,
  } = props;

  const { utils } = useSharedUtilsFromGcfPlayer();
  const PreviewController = utils?.PreviewController;
  const mapScreensToRotationItems = utils?.mapScreensToRotationItems;

  const items = useMemo(
    () =>
      mapScreensToRotationItems
        ? extractRotationItems(previewData, mapScreensToRotationItems)
        : [],
    [previewData, mapScreensToRotationItems],
  );

  const hasPreviewAssembly =
    Object.values(previewData?.activeAssembly?.screens ?? {}).length > 0;

  const effectiveItems = hasPreviewAssembly ? items : [];
  const isFallback = effectiveItems.length === 0;

  const lastKnownPositionRef = useRef(initialPosition);

  // Keep initialPosition stable while engine is mounted — gcf-player's usePreviewEngine
  // resets the engine when initialPosition changes.
  const mountPositionRef = useRef(lastKnownPositionRef.current);

  const pendingPositionRef = useRef<number | undefined>(undefined);

  const controllerRef = useRef<PreviewEngineActions | null>(null);

  // Can't read signals directly in a useEffect
  const currentStatusRef = useRef<RotationEngineStatus>('STOPPED');

  const handleStatusChange = (status: RotationEngineStatus) => {
    const wasPlaying = currentStatusRef.current === 'PLAYING';
    currentStatusRef.current = status;
    onStatusChange?.(status);

    // Apply any position change that was blocked during playback
    if (
      wasPlaying &&
      status !== 'PLAYING' &&
      pendingPositionRef.current !== undefined &&
      controllerRef.current
    ) {
      controllerRef.current.jump(pendingPositionRef.current);
      pendingPositionRef.current = undefined;
    }
  };

  useEffect(() => {
    if (initialPosition !== undefined) {
      lastKnownPositionRef.current = initialPosition;
    }
  }, [initialPosition]);

  // When initialPosition changes while already in fallback, update mountPositionRef
  // so the engine remounts at the new position when data comes back.
  // On the first fallback render, mountPositionRef was already set from lastKnownPositionRef
  // (which may be ahead of initialPosition after an internal jump), so we skip it.
  const prevInitialPositionRef = useRef(initialPosition);
  useEffect(() => {
    if (
      isFallback &&
      initialPosition !== prevInitialPositionRef.current &&
      initialPosition !== undefined
    ) {
      mountPositionRef.current = initialPosition;
    }
    prevInitialPositionRef.current = initialPosition;
  }, [isFallback, initialPosition]);

  // When the engine unmounts (items gone), it can't fire onStatusChange — reset manually
  // so that pending-position and status bookkeeping stay consistent.
  useEffect(() => {
    if (!isFallback) return;
    if (currentStatusRef.current === 'STOPPED') return;

    currentStatusRef.current = 'STOPPED';
    pendingPositionRef.current = undefined;
    onStatusChange?.('STOPPED');
  }, [isFallback, onStatusChange]);

  // Skip redundant jump() calls when URL syncs back to the position the engine is already at
  const lastExternalJumpRef = useRef<number | undefined>(undefined);

  // When items reference changes, the gcf-player engine resets to
  // initialPosition. Update mountPositionRef so the engine resets to the
  // current position instead of the stale mount-time value (1).
  const prevItemsRef = useRef(effectiveItems);
  if (effectiveItems.length > 0 && effectiveItems !== prevItemsRef.current) {
    prevItemsRef.current = effectiveItems;
    mountPositionRef.current = lastKnownPositionRef.current;
  }

  const handleInternalActivePositionChange = useCallback(
    (position: number) => {
      lastKnownPositionRef.current = position;
      lastExternalJumpRef.current = position;
      onActivePositionChange?.(position);
    },
    [onActivePositionChange],
  );

  // Skip first run — PreviewController handles initial position via its prop
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastExternalJumpRef.current = initialPosition;

      return;
    }

    if (initialPosition === undefined || !controllerRef.current) {
      return;
    }

    if (lastExternalJumpRef.current === initialPosition) {
      return;
    }

    if (currentStatusRef.current === 'PLAYING') {
      pendingPositionRef.current = initialPosition;

      return;
    }

    controllerRef.current.jump(initialPosition);
    lastExternalJumpRef.current = initialPosition;
    pendingPositionRef.current = undefined;
  }, [initialPosition]);

  if (!PreviewController) {
    return null;
  }

  if (effectiveItems.length === 0) {
    mountPositionRef.current = lastKnownPositionRef.current;

    return (
      <Fragment>
        {children({
          status: 'STOPPED',
          // Prefer the last confirmed internal position; initialPosition may lag behind
          // after an engine jump that hasn't yet round-tripped to the URL.
          activePosition: lastKnownPositionRef.current ?? 1,
          totalPositions: 0,
          positionGroups: [],
          isNextReady: false,
          enableFadeTransition: false,
          play: () => {},
          pause: () => {},
          resume: () => {},
          jump: () => {},
        })}
      </Fragment>
    );
  }

  return (
    <PreviewController
      ref={controllerRef}
      items={effectiveItems}
      autoPlay={false}
      initialPosition={mountPositionRef.current}
      products={previewData?.mappedProducts}
      onStatusChange={handleStatusChange}>
      {(signals) => (
        <Fragment>
          <PositionSync
            signals={signals}
            onActivePositionChange={handleInternalActivePositionChange}
          />
          {children(signals)}
        </Fragment>
      )}
    </PreviewController>
  );
};
